import {
  Page, Layout, Card, BlockStack, InlineStack, Text, Badge, Banner,
  Spinner, DataTable, Tabs,
} from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getCollectionsPreview, type PreviewEntry } from '../api/collections-preview';

export function CollectionsPreview() {
  const q = useQuery({
    queryKey: ['collections-preview'],
    queryFn: getCollectionsPreview,
    refetchInterval: 60_000,
  });
  const [tab, setTab] = useState(0);

  if (q.isLoading) return <Page title="Collections Preview"><Spinner /></Page>;
  if (q.error) return <Page title="Collections Preview"><Banner tone="critical">{(q.error as Error).message}</Banner></Page>;
  const data = q.data!;

  const tabs = data.groups.map((g, i) => ({ id: `g-${i}`, content: g }));

  // Only show entries for the selected group.
  const selectedGroup = data.groups[tab];
  const entries = data.entries.filter((e) => e.group === selectedGroup);

  // Sort by product_count desc, empty/warned at the bottom of the list.
  entries.sort((a, b) => (b.product_count - a.product_count));

  const rows = entries.map((e: PreviewEntry) => [
    e.name,
    <code style={{ fontSize: 11 }}>{e.tag_rule}</code>,
    e.staged_only
      ? <Badge tone="attention">Staged only</Badge>
      : e.product_count === 0
        ? <Badge tone="warning">0 — empty</Badge>
        : <Badge tone="success">{e.product_count.toLocaleString()}</Badge>,
    e.vendor_mix.map((v) => `${v.source_guide}:${v.count}`).join(' · ') || '—',
    e.example_skus.slice(0, 3).join(', ') || '—',
  ]);

  // Top 20 by count across all groups for the headline panel.
  const top20 = [...data.entries]
    .filter((e) => !e.staged_only)
    .sort((a, b) => b.product_count - a.product_count)
    .slice(0, 20);

  return (
    <Page
      title="Collections Preview"
      subtitle="What Shopify Smart Collections we'd create when products ship — read-only, nothing pushes"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <InlineStack gap="400">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Total planned collections: <strong>{data.total_planned.toLocaleString()}</strong>
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    Groups: <strong>{data.total_groups}</strong>
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    Empty (would create 0-product collections): <strong>{data.total_empty}</strong>
                  </Text>
                </InlineStack>
                {data.total_empty > 0 && (
                  <Banner tone="warning">
                    {data.total_empty} planned collections currently match 0 products.
                    They'd create empty smart collections in Shopify if you push as-is.
                    Either skip those rules or wait until products with the matching tags ship.
                  </Banner>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">Top 20 collections by product count</Text>
                <DataTable
                  columnContentTypes={['text','text','numeric','text']}
                  headings={['Name','Tag rule','Products','Vendor mix']}
                  rows={top20.map((e) => [
                    e.name,
                    <code style={{ fontSize: 11 }}>{e.tag_rule}</code>,
                    e.product_count.toLocaleString(),
                    e.vendor_mix.map((v) => `${v.source_guide}:${v.count}`).join(' · ') || '—',
                  ])}
                />
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">By group</Text>
                <Tabs tabs={tabs} selected={tab} onSelect={setTab}>
                  <BlockStack gap="200">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {entries.length} collection{entries.length === 1 ? '' : 's'} planned in <strong>{selectedGroup}</strong>
                    </Text>
                    <DataTable
                      columnContentTypes={['text','text','numeric','text','text']}
                      headings={['Collection name','Tag rule','Products','Vendor mix','Example SKUs']}
                      rows={rows}
                    />
                  </BlockStack>
                </Tabs>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Tagging spec (locked)</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Every wheel pushed to Shopify will receive this tag set. Smart Collections key off the tags:
                </Text>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, overflow: 'auto' }}>
{`type:wheel
brand:{slug}
diameter:{n}
width:{n-with-dash}
bolt:{slug}
finish:{slug}
vehicle_type:{truck|suv|car|offroad|dually}
install_speed:{same_day|next_day|standard|estimated}
source:{wheelpros|atd}
fits-make:{slug}
fits:{vehicle-slug}`}
                </pre>
                <Banner tone="info">
                  No products have been pushed to Shopify yet. Reviewing this preview is the gate;
                  once approved, the same tags are applied during shopify-push, and Smart Collection
                  creation can be safely automated.
                </Banner>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
